import React, { useState, useEffect } from 'react';
import { CreditCard, Receipt, Download, Eye, Clock, CheckCircle, AlertCircle, XCircle, Euro, Calendar, Filter, FileText, ChevronRight, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PaymentManager = ({ patientId }) => {
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('invoices'); // 'invoices', 'payments', 'subscription'
  const [subscription, setSubscription] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchData();
  }, [patientId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch invoices
      const invoicesRes = await fetch(`${API}/api/patient/invoices`, { headers });
      if (invoicesRes.ok) {
        const data = await invoicesRes.json();
        setInvoices(data.invoices || []);
      }

      // Fetch payments
      const paymentsRes = await fetch(`${API}/api/patient/payments`, { headers });
      if (paymentsRes.ok) {
        const data = await paymentsRes.json();
        setPayments(data.payments || []);
      }

      // Fetch subscription
      const subRes = await fetch(`${API}/api/patient/subscription`, { headers });
      if (subRes.ok) {
        const data = await subRes.json();
        setSubscription(data.subscription);
      }
    } catch (error) {
      console.error('Error fetching payment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayInvoice = async (invoiceId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/patient/invoices/${invoiceId}/pay`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (res.ok) {
        const data = await res.json();
        // Redirect to Stripe checkout
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        }
      }
    } catch (error) {
      console.error('Error initiating payment:', error);
    }
  };

  const handleDownloadInvoice = async (invoice) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/patient/invoices/${invoice.id}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `fattura_${invoice.invoice_number}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
    }
  };

  const getStatusInfo = (status) => {
    switch(status) {
      case 'paid':
        return { label: 'Pagata', color: 'bg-green-100 text-green-700', icon: CheckCircle };
      case 'pending':
        return { label: 'In Attesa', color: 'bg-amber-100 text-amber-700', icon: Clock };
      case 'pending_acceptance':
        return { label: 'Da Pagare', color: 'bg-amber-100 text-amber-700', icon: Clock };
      case 'overdue':
        return { label: 'Scaduta', color: 'bg-red-100 text-red-700', icon: AlertCircle };
      case 'cancelled':
        return { label: 'Annullata', color: 'bg-gray-100 text-gray-700', icon: XCircle };
      default:
        return { label: status, color: 'bg-gray-100 text-gray-700', icon: Clock };
    }
  };

  const getInvoiceAmount = (inv) => {
    return inv.total || inv.amount || inv.amount_to_pay || 0;
  };

  const filteredInvoices = filterStatus === 'all' 
    ? invoices 
    : invoices.filter(inv => inv.status === filterStatus);

  const totalPending = invoices
    .filter(inv => inv.status === 'pending' || inv.status === 'pending_acceptance' || inv.status === 'overdue')
    .reduce((sum, inv) => sum + getInvoiceAmount(inv), 0);

  const totalPaid = invoices
    .filter(inv => inv.status === 'paid')
    .reduce((sum, inv) => sum + getInvoiceAmount(inv), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2A9FCB]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-semibold" style={{color: '#2A9FCB', fontFamily: "'Lora', Georgia, serif"}}>
          Pagamenti e Fatturazione
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Gestisci i tuoi pagamenti, visualizza le fatture e controlla l'abbonamento
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{background: '#FEF3C7'}}>
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Da Pagare</p>
              <p className="text-2xl font-bold text-amber-600">€{totalPending.toFixed(2)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{background: '#D1FAE5'}}>
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Pagato (Totale)</p>
              <p className="text-2xl font-bold text-green-600">€{totalPaid.toFixed(2)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{background: '#E8F4FD'}}>
              <Receipt className="w-6 h-6 text-[#2A9FCB]" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Fatture Totali</p>
              <p className="text-2xl font-bold" style={{color: '#2A9FCB'}}>{invoices.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
        {[
          { id: 'invoices', label: 'Fatture', icon: Receipt },
          { id: 'payments', label: 'Pagamenti', icon: CreditCard },
          { id: 'subscription', label: 'Abbonamento', icon: Calendar }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id ? 'bg-white shadow-sm text-[#2A9FCB]' : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <div className="space-y-4">
          {/* Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            {['all', 'pending', 'paid', 'overdue'].map(status => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                  filterStatus === status ? 'bg-[#2A9FCB] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {status === 'all' ? 'Tutte' : getStatusInfo(status).label}
              </button>
            ))}
          </div>

          {filteredInvoices.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <Receipt className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Nessuna fattura {filterStatus !== 'all' && `con stato "${getStatusInfo(filterStatus).label}"`}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredInvoices.map(invoice => {
                const statusInfo = getStatusInfo(invoice.status);
                const StatusIcon = statusInfo.icon;
                return (
                  <div 
                    key={invoice.id} 
                    className="bg-white rounded-xl border p-4 hover:shadow-md transition-all"
                    data-testid={`invoice-${invoice.id}`}
                  >
                    <div className="flex flex-col sm:flex-row justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{background: '#E8F4FD'}}>
                          <FileText className="w-6 h-6" style={{color: '#2A9FCB'}} />
                        </div>
                        <div>
                          <h3 className="font-semibold" style={{color: '#2A9FCB'}}>{invoice.invoice_code || invoice.invoice_number}</h3>
                          <p className="text-sm text-gray-500">{invoice.description || 'Prestazione sanitaria'}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            Emessa: {new Date(invoice.issue_date || invoice.created_at).toLocaleDateString('it-IT')}
                            {invoice.due_date && ` • Scadenza: ${new Date(invoice.due_date).toLocaleDateString('it-IT')}`}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex flex-col items-end gap-2">
                        <span className="text-xl font-bold" style={{color: '#2A9FCB'}}>€{getInvoiceAmount(invoice).toFixed(2)}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusInfo.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusInfo.label}
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-2 mt-4 pt-4 border-t">
                      <button
                        onClick={() => setSelectedInvoice(invoice)}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg hover:bg-gray-100 transition-colors"
                        style={{color: '#2A9FCB'}}
                      >
                        <Eye className="w-4 h-4" /> Dettagli
                      </button>
                      <button
                        onClick={() => handleDownloadInvoice(invoice)}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg hover:bg-gray-100 transition-colors"
                        style={{color: '#2A9FCB'}}
                      >
                        <Download className="w-4 h-4" /> PDF
                      </button>
                      {(invoice.status === 'pending' || invoice.status === 'pending_acceptance' || invoice.status === 'overdue') && (
                        <button
                          onClick={() => handlePayInvoice(invoice.id)}
                          className="flex items-center gap-1 px-4 py-1.5 text-sm rounded-lg text-white ml-auto transition-opacity hover:opacity-90"
                          style={{background: 'linear-gradient(135deg, #2A9FCB, #2A9FCB)'}}
                          data-testid="pay-now-btn"
                        >
                          <CreditCard className="w-4 h-4" /> Paga Ora
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Payments Tab */}
      {activeTab === 'payments' && (
        <div className="space-y-4">
          {payments.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <CreditCard className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Nessun pagamento registrato</p>
            </div>
          ) : (
            <div className="space-y-3">
              {payments.map(payment => (
                <div 
                  key={payment.id} 
                  className="bg-white rounded-xl border p-4"
                  data-testid={`payment-${payment.id}`}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        payment.payment_status === 'paid' ? 'bg-green-100' : 'bg-amber-100'
                      }`}>
                        {payment.payment_status === 'paid' ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <Clock className="w-5 h-5 text-amber-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium" style={{color: '#2A9FCB'}}>
                          {payment.payment_method === 'stripe' ? 'Carta di Credito' : 
                           payment.payment_method === 'cash' ? 'Contanti' : 'Bonifico'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(payment.created_at).toLocaleString('it-IT')}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold" style={{color: '#2A9FCB'}}>€{(payment.amount || 0).toFixed(2)}</p>
                      <p className="text-xs text-gray-500">{payment.invoice_number || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Subscription Tab */}
      {activeTab === 'subscription' && (
        <div className="space-y-4">
          {!subscription ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <Calendar className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 mb-2">Nessun abbonamento attivo</p>
              <p className="text-sm text-gray-400 mb-4">Scopri i nostri piani per risparmiare sulle visite</p>
              <button
                onClick={() => window.location.href = '/subscriptions'}
                className="px-6 py-2 rounded-lg text-white transition-opacity hover:opacity-90"
                style={{background: 'linear-gradient(135deg, #2A9FCB, #2A9FCB)'}}
              >
                Scopri gli Abbonamenti
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-xl border p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="text-xl font-semibold" style={{color: '#2A9FCB'}}>{subscription.plan_name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{subscription.description}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  subscription.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                }`}>
                  {subscription.status === 'active' ? 'Attivo' : 'Non Attivo'}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-4 rounded-lg" style={{background: '#E8F4FD'}}>
                  <p className="text-sm text-gray-500">Data Inizio</p>
                  <p className="font-semibold" style={{color: '#2A9FCB'}}>
                    {new Date(subscription.start_date).toLocaleDateString('it-IT')}
                  </p>
                </div>
                <div className="p-4 rounded-lg" style={{background: '#E8F4FD'}}>
                  <p className="text-sm text-gray-500">Scadenza</p>
                  <p className="font-semibold" style={{color: '#2A9FCB'}}>
                    {new Date(subscription.end_date).toLocaleDateString('it-IT')}
                  </p>
                </div>
              </div>

              {subscription.features && (
                <div>
                  <h4 className="font-medium mb-3" style={{color: '#2A9FCB'}}>Servizi Inclusi:</h4>
                  <ul className="space-y-2">
                    {subscription.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-xl font-semibold" style={{color: '#2A9FCB'}}>
                Fattura {selectedInvoice.invoice_code || selectedInvoice.invoice_number}
              </h3>
              <button onClick={() => setSelectedInvoice(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                ×
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex justify-between items-center pb-4 border-b">
                <span className="text-gray-500">Stato</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusInfo(selectedInvoice.status).color}`}>
                  {getStatusInfo(selectedInvoice.status).label}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Data Emissione</span>
                <span>{new Date(selectedInvoice.issue_date || selectedInvoice.created_at).toLocaleDateString('it-IT')}</span>
              </div>
              {selectedInvoice.due_date && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Scadenza</span>
                  <span>{new Date(selectedInvoice.due_date).toLocaleDateString('it-IT')}</span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Descrizione</span>
                <span>{selectedInvoice.description || 'Prestazione sanitaria'}</span>
              </div>
              
              {selectedInvoice.items && selectedInvoice.items.length > 0 && (
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-2">Dettaglio Voci:</h4>
                  {selectedInvoice.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm py-1">
                      <span>{item.description}</span>
                      <span>€{(item.amount || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-between items-center pt-4 border-t">
                <span className="font-semibold">Totale</span>
                <span className="text-xl font-bold" style={{color: '#2A9FCB'}}>€{getInvoiceAmount(selectedInvoice).toFixed(2)}</span>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => handleDownloadInvoice(selectedInvoice)}
                  className="flex-1 py-3 border rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" /> Scarica PDF
                </button>
                {(selectedInvoice.status === 'pending' || selectedInvoice.status === 'overdue') && (
                  <button
                    onClick={() => handlePayInvoice(selectedInvoice.id)}
                    className="flex-1 py-3 rounded-lg font-medium text-white transition-opacity hover:opacity-90 flex items-center justify-center gap-2"
                    style={{background: 'linear-gradient(135deg, #2A9FCB, #2A9FCB)'}}
                  >
                    <CreditCard className="w-4 h-4" /> Paga Ora
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentManager;
